/**
 * Capture / upload de photo justificative pour un écart d'inventaire.
 * - Bouton "Prendre photo" → input file caméra (capture=environment)
 * - Compression côté client via canvas (~max 1280px, qualité 0.7)
 * - Upload via POST /api/inventory/upload-evidence
 *
 * Props :
 * - photoUrl                       : URL existante (mode lecture/édition)
 * - onUploaded(url: string|null)   : callback nouvelle URL (ou null si retiré)
 * - disabled                       : désactive l'interaction
 */
import React, { useRef, useState } from 'react';
import axios from 'axios';
import { Camera, Loader2, X, Check } from 'lucide-react';
import { Button } from '../../ui/button';
import { toast } from 'sonner';
import { preprocessEvidence } from '../../../utils/imagePreprocess';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const auth = () => {
  const t = localStorage.getItem('dermascan_token');
  return t ? { Authorization: `Bearer ${t}` } : {};
};

const EvidencePhotoUploader = ({ photoUrl, onUploaded, disabled }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      // Pré-traitement : 1600px + EXIF auto-orient + JPEG 0.85 (couleur préservée
      // pour identification visuelle, pas d'OCR donc pas besoin de niveaux de gris)
      const { blob } = await preprocessEvidence(file);
      const compressed = new File([blob], 'evidence.jpg', { type: 'image/jpeg' });
      const fd = new FormData();
      fd.append('file', compressed, 'evidence.jpg');
      const res = await axios.post(
        `${API}/inventory/upload-evidence`, fd,
        { headers: { ...auth(), 'Content-Type': 'multipart/form-data' } }
      );
      onUploaded(res.data.photo_url);
      toast.success('Photo ajoutée');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de l\'envoi');
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  // L'URL est servie sous /api/uploads/...
  const fullUrl = photoUrl
    ? (photoUrl.startsWith('http') ? photoUrl : `${process.env.REACT_APP_BACKEND_URL}/api${photoUrl}`)
    : null;

  if (fullUrl) {
    return (
      <div className="space-y-2">
        <div className="relative inline-block">
          <img
            src={fullUrl}
            alt="Justificatif"
            className="max-h-40 rounded-md border border-zinc-200"
            data-testid="evidence-photo-preview"
          />
          {!disabled && (
            <button
              type="button"
              onClick={() => onUploaded(null)}
              className="absolute -top-2 -right-2 bg-white border border-zinc-300 rounded-full p-1 shadow hover:bg-red-50"
              title="Retirer la photo"
              data-testid="evidence-photo-remove"
            >
              <X className="w-3.5 h-3.5 text-red-600" />
            </button>
          )}
        </div>
        <div className="flex items-center gap-1.5 text-xs text-emerald-700">
          <Check className="w-3.5 h-3.5" /> Justificatif joint
        </div>
      </div>
    );
  }

  return (
    <div>
      <input
        ref={fileRef}
        type="file"
        accept="image/*"
        capture="environment"
        className="hidden"
        onChange={handleFile}
        disabled={disabled || uploading}
        data-testid="evidence-photo-input"
      />
      <Button
        type="button"
        variant="outline"
        onClick={() => fileRef.current?.click()}
        disabled={disabled || uploading}
        className="border-purple-300 text-[#5B3B8C] hover:bg-purple-50"
        data-testid="evidence-photo-btn"
      >
        {uploading
          ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> Envoi…</>
          : <><Camera className="w-4 h-4 mr-1.5" /> Prendre photo justificative</>}
      </Button>
    </div>
  );
};

export default EvidencePhotoUploader;
